import * as React from 'react';
import { createPortal } from 'react-dom';
import { Users, X, Plus, Minus, Flag, Circle, Loader2, UserPlus, Play } from 'lucide-react';
import { Button } from './ui/Button';
import IgygRepBlocksPage from './IgygRepBlocksPage';
import { useSwipeTabs } from '../hooks/useSwipeTabs';
import { useLocale } from '../context/LocaleContext';
import type { PlannedTrainingDTO } from '../context/StartedTrainingContext';
import { serverNow, syncServerClock } from '../lib/clockSync';
import { authFetch } from '../lib/authFetch';
import { API_BASE } from '../lib/apiBase';
import {
  joinPartnerChannel,
  sendPartnerInvite,
  type PartnerChannelHandle,
  type PartnerReps,
} from '../lib/partnerSession';

export type PanelExercise = { id: string; name: string; target: number | null };

type Props = {
  training: PlannedTrainingDTO;
  blockId: string;
  blockTitle: string;
  exercises: PanelExercise[];
  isAmrap: boolean;
  durationMin: number | null;
  selfUserId: string;
  selfName: string;
  hasNextMethod: boolean;
  onAdvance: () => void;
  onAbort: () => void;
};

function fmtTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function makeSessionId(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Host-side "I Go You Go" panel, shown by StartedTrainingCard when the current
 * block is an IGYG block. The host runs the normal workout and, on reaching this
 * block, can invite a friend who joins straight into the shared block (guest view
 * = PartnerStartedCard). Shared timer + combined reps ride the partner channel
 * (lib/partnerSession.ts, host-authoritative, no backend). Advancing calls back
 * into the normal Started flow (onAdvance) so later methods continue solo.
 *
 * Restyled to match StartedTrainingCard; deeper per-set integration is a later step.
 */
export default function IgygHostPanel({
  training, blockId, blockTitle, exercises, isAmrap, durationMin,
  selfUserId, selfName, hasNextMethod, onAdvance, onAbort,
}: Props) {
  const { t } = useLocale();

  const [sessionId] = React.useState(makeSessionId);
  const [startedAtServerMs, setStartedAtServerMs] = React.useState<number | null>(null);
  const [reps, setReps] = React.useState<PartnerReps>({});
  const [members, setMembers] = React.useState<Array<{ userId: string; name: string }>>([]);
  const [now, setNow] = React.useState(() => serverNow());
  // Set when the browser blocks the realtime socket — the block still runs, just
  // without a partner.
  const [syncBlocked, setSyncBlocked] = React.useState(false);

  // Two swipeable pages: the fine +/- counters and the big "knock down" blocks.
  const [page, setPage] = React.useState<'counter' | 'blocks'>('counter');
  const pageSwipe = useSwipeTabs(['counter', 'blocks'] as const, page, setPage);

  // Friend picker
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [friends, setFriends] = React.useState<Array<{ userId: string; username: string | null }>>([]);
  const [friendsLoading, setFriendsLoading] = React.useState(false);
  const [invitedName, setInvitedName] = React.useState<string | null>(null);

  const startedRef = React.useRef<number | null>(null);
  const repsRef = React.useRef<PartnerReps>({});
  const handleRef = React.useRef<PartnerChannelHandle | null>(null);
  startedRef.current = startedAtServerMs;
  repsRef.current = reps;

  const broadcastState = React.useCallback(() => {
    handleRef.current?.sendState({
      training,
      startedAtServerMs: startedRef.current,
      reps: repsRef.current,
      finished: false,
    });
  }, [training]);

  // Join the channel as host once; anchor the shared timer to now (the block is
  // already live inside the host's running workout).
  React.useEffect(() => {
    void syncServerClock();
    const at = serverNow();
    setStartedAtServerMs(at);
    startedRef.current = at;
    const handle = joinPartnerChannel(
      sessionId,
      { userId: selfUserId, name: selfName },
      {
        onUnavailable: () => setSyncBlocked(true),
        onReady: () => broadcastState(),
        onHello: () => broadcastState(),
        onRep: ({ userId, exerciseId, delta }) => {
          if (userId === selfUserId) return; // our taps are applied optimistically
          setReps((prev) => {
            const ex = { ...(prev[exerciseId] || {}) };
            ex[userId] = (ex[userId] || 0) + delta;
            return { ...prev, [exerciseId]: ex };
          });
        },
        onPresence: (m) => setMembers(m),
      },
    );
    handleRef.current = handle;
    return () => { handle.sendFinish(); handle.leave(); handleRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Tick the shared timer.
  React.useEffect(() => {
    if (startedAtServerMs == null) return;
    const id = window.setInterval(() => setNow(serverNow()), 250);
    return () => window.clearInterval(id);
  }, [startedAtServerMs]);

  const openPicker = async () => {
    setPickerOpen(true);
    setFriendsLoading(true);
    setFriends([]);
    try {
      const res = await authFetch(`${API_BASE}/api/friends`);
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data?.friends) ? data.friends : [];
      setFriends(list.map((f: any) => ({ userId: String(f.userId), username: f.username ?? null })));
    } catch {
      setFriends([]);
    } finally {
      setFriendsLoading(false);
    }
  };

  const invite = (friend: { userId: string; username: string | null }) => {
    setPickerOpen(false);
    setInvitedName(friend.username ?? t('Partner', 'Partner'));
    void sendPartnerInvite(friend.userId, {
      sessionId,
      hostUserId: selfUserId,
      hostName: selfName,
      trainingTitle: blockTitle || training.title || '',
      // Carry the specific block so the guest opens the same one (a training can
      // have more than one IGYG block).
      training: { ...(training as any), igygBlockId: blockId },
    });
    // Re-broadcast shortly after so the guest hydrates on arrival.
    window.setTimeout(() => broadcastState(), 400);
  };

  const addRep = (exerciseId: string, delta: number) => {
    setReps((prev) => {
      const ex = { ...(prev[exerciseId] || {}) };
      const next = Math.max(0, (ex[selfUserId] || 0) + delta);
      ex[selfUserId] = next;
      return { ...prev, [exerciseId]: ex };
    });
    handleRef.current?.sendRep({ userId: selfUserId, exerciseId, delta });
  };

  const advance = () => { handleRef.current?.sendFinish(); onAdvance(); };

  // ── Derived ──
  const elapsedMs = startedAtServerMs != null ? Math.max(0, now - startedAtServerMs) : 0;
  const amrapCapMs = isAmrap && durationMin ? durationMin * 60 * 1000 : null;
  const timeUp = amrapCapMs != null && elapsedMs >= amrapCapMs;
  const timerMs = amrapCapMs != null ? Math.max(0, amrapCapMs - elapsedMs) : elapsedMs;

  const combinedFor = (exId: string) => Object.values(reps[exId] || {}).reduce((a, b) => a + b, 0);
  const partnerId = members.find((m) => m.userId !== selfUserId)?.userId ?? null;
  const partnerName = invitedName || members.find((m) => m.userId !== selfUserId)?.name || t('Partner', 'Partner');
  const selfOnline = members.some((m) => m.userId === selfUserId);
  const partnerOnline = !!partnerId;

  return (
    <div
      className="fixed inset-0 z-[1001] flex flex-col bg-white dark:bg-slate-950"
      style={{ paddingTop: 'var(--safe-top, 0px)', paddingBottom: 'var(--safe-bottom, 0px)' }}
    >
      <div className="relative w-full flex-1 min-h-0 overflow-hidden flex flex-col bg-white dark:bg-slate-950">
        {/* ── Header ── */}
        <div className="px-4 py-3 flex items-center gap-3 shrink-0 border-b border-gray-200/60 dark:border-white/10 bg-gray-50/60 dark:bg-white/[0.03]">
          <Users size={18} className="text-brand-600 dark:text-brand-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{blockTitle || t('Workout', 'Workout')}</div>
            <div className="text-[11px] opacity-40 truncate">{t('I Go You Go', 'I Go You Go')}</div>
          </div>
          <button type="button" onClick={onAbort} aria-label={t('Close', 'Close')} className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-white/10 transition text-gray-500 dark:text-gray-400">
            <X size={18} />
          </button>
        </div>

        {/* ── Body (swipe between the fine counters and the knock-down blocks) ── */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden" ref={pageSwipe.ref} onTouchStart={pageSwipe.onTouchStart} onTouchEnd={pageSwipe.onTouchEnd}>
          {page === 'blocks' ? (
            <IgygRepBlocksPage exercises={exercises} combinedFor={combinedFor} onKnockDown={addRep} t={t} />
          ) : (
          <div className="flex flex-col gap-2 px-4 pb-4 pt-3 min-h-full">

            {/* Presence */}
            <div className="flex items-center justify-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <Circle size={9} className={selfOnline ? 'text-emerald-500 fill-emerald-500' : 'text-gray-300 fill-gray-300'} />
                {selfName} <span className="opacity-40">({t('you', 'you')})</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Circle size={9} className={partnerOnline ? 'text-emerald-500 fill-emerald-500' : 'text-gray-300 fill-gray-300 animate-pulse'} />
                {invitedName || partnerName} {!partnerOnline && <span className="opacity-40">({invitedName ? t('waiting…', 'waiting…') : t('not invited', 'not invited')})</span>}
              </span>
            </div>

            {/* ── Timer card ── */}
            <div className="rounded-2xl border border-gray-200/60 dark:border-white/10 overflow-hidden">
              <div className="relative px-4 pt-3 pb-4 text-center">
                <div className="flex justify-center mb-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                    <Users size={11} /> {t('I Go You Go', 'I Go You Go')}
                  </span>
                </div>
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-40 mb-1.5">
                  {amrapCapMs != null ? t('AMRAP Countdown', 'AMRAP Countdown') : t('Workout Timer', 'Workout Timer')}
                </div>
                <div className={['text-6xl sm:text-7xl font-black tabular-nums tracking-tight leading-none', timeUp ? 'text-rose-500' : ''].join(' ')}>
                  {fmtTime(timerMs)}
                </div>
              </div>
            </div>

            {/* Realtime blocked — say so instead of showing an invite that
                silently does nothing. */}
            {syncBlocked && (
              <div className="rounded-2xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-center text-xs text-amber-800 dark:text-amber-200">
                <div className="font-semibold mb-0.5">{t('Partner sync unavailable', 'Partner sync unavailable')}</div>
                {t(
                  'This browser blocked the live connection. The block still works solo — reps are counted on this device only.',
                  'This browser blocked the live connection. The block still works solo — reps are counted on this device only.',
                )}
              </div>
            )}

            {/* Invite prompt (until a partner is connected) */}
            {!partnerOnline && !syncBlocked && (
              <button
                type="button"
                onClick={openPicker}
                className="w-full rounded-2xl border border-dashed border-brand-300 dark:border-brand-700 bg-brand-50/60 dark:bg-brand-900/10 px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition"
              >
                <UserPlus size={16} /> {invitedName ? t('Invite another partner', 'Invite another partner') : t('Invite partner', 'Invite partner')}
              </button>
            )}

            {/* ── Combined rep counters ── */}
            {exercises.map((e) => {
              const combined = combinedFor(e.id);
              const mine = reps[e.id]?.[selfUserId] || 0;
              const theirs = partnerId ? (reps[e.id]?.[partnerId] || 0) : Math.max(0, combined - mine);
              return (
                <div key={e.id} className="rounded-2xl border border-gray-200/60 dark:border-white/10 p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="font-medium text-sm truncate">{e.name}</div>
                    <div className="text-xs opacity-50 tabular-nums">
                      {combined}{e.target ? ` / ${e.target}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => addRep(e.id, -1)}
                      disabled={mine <= 0}
                      className="h-11 w-11 rounded-full border-2 border-brand-300 dark:border-brand-700 text-xl font-bold text-brand-700 dark:text-brand-300 grid place-items-center disabled:opacity-30 active:scale-95 transition"
                      aria-label={t('Remove one', 'Remove one')}
                    >
                      <Minus size={18} />
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-3xl font-black tabular-nums leading-none">{combined}</div>
                      <div className="text-[11px] opacity-50 mt-1">
                        {t('You', 'You')} {mine} · {partnerName} {theirs}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => addRep(e.id, 1)}
                      className="h-11 w-11 rounded-full bg-brand-500 text-white text-xl font-bold grid place-items-center active:scale-95 transition"
                      aria-label={t('Add one', 'Add one')}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
            {exercises.length === 0 && (
              <div className="text-sm opacity-60 px-3 py-6 text-center">{t('No exercises in this block.', 'No exercises in this block.')}</div>
            )}
          </div>
          )}
        </div>

        {/* ── Page dots ── */}
        <div className="shrink-0 flex justify-center gap-2 pt-1.5">
          {(['counter', 'blocks'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              aria-label={p === 'counter' ? t('Counters', 'Counters') : t('Knock down reps', 'Knock down reps')}
              aria-current={page === p ? 'page' : undefined}
              className={['rounded-full transition-all', page === p ? 'w-2 h-2 bg-slate-700 dark:bg-slate-200' : 'w-1.5 h-1.5 bg-slate-400/60 dark:bg-slate-500/60'].join(' ')}
            />
          ))}
        </div>

        {/* ── Footer: advance the solo workout ── */}
        <div className="p-3 shrink-0 border-t border-gray-200/60 dark:border-white/10">
          <Button variant="primary" className="w-full justify-center" onClick={advance}>
            {hasNextMethod ? (<><Play size={16} /> {t('Next Method', 'Next Method')}</>) : (<><Flag size={16} /> {t('Finish Workout', 'Finish Workout')}</>)}
          </Button>
        </div>
      </div>

      {/* Friend picker */}
      {pickerOpen && createPortal(
        <div className="fixed inset-0 z-[9998] grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPickerOpen(false)} />
          <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
              <Users size={16} className="text-brand-600 dark:text-brand-400" />
              <div className="font-semibold text-sm">{t('Invite partner', 'Invite partner')}</div>
              <button type="button" className="ml-auto p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => setPickerOpen(false)} aria-label={t('Close', 'Close')}>
                <X size={16} />
              </button>
            </div>
            <div className="p-2 max-h-[50vh] overflow-auto">
              {friendsLoading ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm opacity-70"><Loader2 size={16} className="animate-spin" /> {t('Loading…', 'Loading…')}</div>
              ) : friends.length === 0 ? (
                <div className="px-3 py-4 text-sm opacity-70">{t('No friends yet — add friends to train together.', 'No friends yet — add friends to train together.')}</div>
              ) : (
                friends.map((f) => (
                  <button
                    key={f.userId}
                    type="button"
                    onClick={() => invite(f)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 text-left"
                  >
                    <span className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 grid place-items-center text-brand-600 dark:text-brand-400 text-sm font-bold flex-shrink-0">
                      {(f.username || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="font-medium text-sm truncate">{f.username || t('Friend', 'Friend')}</span>
                    <UserPlus size={14} className="ml-auto opacity-40 flex-shrink-0" />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
